import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXS } from '@sorare/core/src/atoms/typography/index.tsx';

import { PickerOption } from '#src/components/pickers/BoardPicker/PickerCard.tsx';
import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';
import { PickerOptionsGrid } from '#src/components/pickers/PickerOptionsGrid.tsx';

import type { LeaderboardPicker_so5Leaderboard } from './__generated__/index.graphql.ts';

const StyledPickerOption = styled(PickerOption)`
  padding: var(--triple-unit);
`;

const GroupIcon = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;

  &.md {
    width: var(--quadruple-unit);
    height: var(--quadruple-unit);
    margin-top: var(--intermediate-unit);
  }
`;

const GridIcon = styled.img`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  object-fit: contain;
`;

type Props = {
  leaderboards: readonly LeaderboardPicker_so5Leaderboard[];
  selectedLeaderboard: LeaderboardPicker_so5Leaderboard;
  onSelect: (slug: string) => void;
} & Pick<ComponentProps<typeof ShieldButton>, 'size'>;

export const LeaderboardPicker = ({
  leaderboards,
  selectedLeaderboard,
  onSelect,
  size = 'sm',
}: Props) => {
  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          aria-label={selectedLeaderboard.format.title}
          size={size}
          icon={
            selectedLeaderboard.format.iconUrl ? (
              <GroupIcon
                className={size === 'md' ? 'md' : undefined}
                src={selectedLeaderboard.format.iconUrl}
                alt=""
                role="presentation"
              />
            ) : undefined
          }
          orientation="up"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="Choose Competition" />
          </PickerHeader>
          <PickerOptionsGrid>
            {leaderboards.map(leaderboard => (
              <StyledPickerOption
                key={leaderboard.slug}
                className={classNames({
                  selected: leaderboard.slug === selectedLeaderboard.slug,
                })}
                onClick={() => {
                  onSelect(leaderboard.slug);
                  closeDropdown();
                }}
              >
                <Vertical center>
                  {!!leaderboard.format.iconUrl && (
                    <GridIcon
                      src={leaderboard.format.iconUrl}
                      alt={leaderboard.format.title}
                    />
                  )}
                  <BrandS color="var(--c-white)">
                    {leaderboard.format.title}
                  </BrandS>
                  {!!leaderboard.format.subtitle && (
                    <BrandXS color="var(--c-nd-600)">
                      {leaderboard.format.subtitle}
                    </BrandXS>
                  )}
                </Vertical>
              </StyledPickerOption>
            ))}
          </PickerOptionsGrid>
        </>
      )}
    </Dropdown>
  );
};

LeaderboardPicker.fragments = {
  so5Leaderboard: gql`
    fragment LeaderboardPicker_so5Leaderboard on So5Leaderboard {
      id
      slug
      format {
        iconUrl
        title
        subtitle
      }
    }
  ` as TypedDocumentNode<LeaderboardPicker_so5Leaderboard>,
};
